export const DATE_PRESETS = {
  today: "Today",
  yesterday: "Yesterday",
  last7: "Last 7 days",
  thisMonth: "This month",
  lastMonth: "Last month",
  last6Months: "Last 6 months",
  custom: "Custom",
};

export function getDateRangeFromPreset(preset) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  let start, end;

  switch (preset) {
    case DATE_PRESETS.today:
      start = new Date(today);
      end = new Date(today);
      end.setHours(23, 59, 59, 999);
      break;
    case DATE_PRESETS.yesterday:
      start = new Date(today);
      start.setDate(start.getDate() - 1);
      end = new Date(start);
      end.setHours(23, 59, 59, 999);
      break;
    case DATE_PRESETS.last7:
      start = new Date(today);
      start.setDate(start.getDate() - 6);
      end = new Date(today);
      end.setHours(23, 59, 59, 999);
      break;
    case DATE_PRESETS.thisMonth:
      start = new Date(today.getFullYear(), today.getMonth(), 1);
      end = new Date(today);
      end.setHours(23, 59, 59, 999);
      break;
    case DATE_PRESETS.lastMonth:
      start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      end = new Date(today.getFullYear(), today.getMonth(), 0, 23, 59, 59, 999);
      break;
    case DATE_PRESETS.last6Months:
      start = new Date(today);
      start.setMonth(start.getMonth() - 5);
      start.setDate(1);
      start.setHours(0, 0, 0, 0);
      end = new Date(today);
      end.setHours(23, 59, 59, 999);
      break;
    default:
      return null;
  }
  return {
    startDate: start.toISOString().slice(0, 10),
    endDate: end.toISOString().slice(0, 10),
  };
}

export const MODULE_OPTIONS = [
  { value: "Invoice", label: "Invoices" },
  { value: "StockAdjustment", label: "Stock Adjustments" },
  { value: "Item", label: "Items" },
  { value: "User", label: "Login Activity" },
  { value: "Purchase", label: "Purchases" },
  { value: "Return", label: "Returns" },
  { value: "Staff", label: "Staff" },
  { value: "Commission", label: "Commission" },
  { value: "Payment", label: "Payments" },
  { value: "Supplier", label: "Suppliers" },
  { value: "Report", label: "Reports" },
  { value: "Settings", label: "Settings" },
  { value: "Security", label: "Security" },
  { value: "System", label: "System" },
];

export const ACTION_OPTIONS = [
  { value: "CREATE", label: "Create" },
  { value: "UPDATE", label: "Update" },
  { value: "DELETE", label: "Delete" },
  { value: "BULK_CREATE", label: "Bulk Create" },
  { value: "BULK_UPDATE", label: "Bulk Update" },
  { value: "SOFT_DELETE", label: "Soft Delete" },
  { value: "LOGIN_SUCCESS", label: "Login" },
  { value: "LOGOUT_SUCCESS", label: "Logout" },
  { value: "SIGNUP_SUCCESS", label: "Signup" },
  { value: "SWITCH_TO_POS", label: "Go to POS" },
  { value: "SWITCH_TO_SUPERADMIN", label: "Go to SuperAdmin" },
  { value: "DISPLAY_TIMEZONE_CHANGED", label: "Change Display Timezone" },
  { value: "APPROVE", label: "Approve" },
  { value: "REJECT", label: "Reject" },
  { value: "REFUND", label: "Refund" },
  { value: "AUTO_APPROVE", label: "Auto-approve" },
  { value: "AUTO_APPROVE_FAILED", label: "Auto-approve failed" },
  { value: "VOID", label: "Void" },
];

export const ROLE_OPTIONS = [
  { value: "superAdmin", label: "Super Admin" },
  { value: "admin", label: "Admin" },
  { value: "user", label: "User" },
];

// Stored userRole values are whatever's first in a user's userType array
// (see server/controllers/user.js signin/logout/signup) - e.g. "superAdmin",
// "admin", or a custom user-type name. Normalizes casing/formatting for
// display and picks a badge style; anything unrecognized falls back to the
// raw value as its own label so custom user types still show correctly.
export const formatRole = (role) => {
  const normalized = String(role || "").trim();
  const lower = normalized.toLowerCase();

  if (lower === "superadmin") {
    return { label: "Super Admin", tier: "superAdmin" };
  }
  if (lower === "admin") {
    return { label: "Admin", tier: "admin" };
  }
  if (!normalized || lower === "user") {
    return { label: "User", tier: "user" };
  }
  // Custom user type (e.g. "Cashier", "Manager") - show as-is.
  return { label: normalized, tier: "user" };
};

export const ROLE_BADGE_STYLES = {
  superAdmin: {
    backgroundColor: "#fdf4ff",
    color: "#a21caf",
    border: "1px solid #f0abfc",
  },
  admin: {
    backgroundColor: "#eff6ff",
    color: "#1e40af",
    border: "1px solid #bfdbfe",
  },
  user: {
    backgroundColor: "#f1f5f9",
    color: "#475569",
    border: "1px solid #e2e8f0",
  },
};

export const ACTION_STYLES = {
  CREATE: {
    backgroundColor: "#e8f5e9",
    color: "#2e7d32",
    border: "1px solid #c8e6c9",
  },
  UPDATE: {
    backgroundColor: "#e3f2fd",
    color: "#1565c0",
    border: "1px solid #bbdefb",
  },
  DELETE: {
    backgroundColor: "#ffebee",
    color: "#c62828",
    border: "1px solid #ffcdd2",
  },
  SOFT_DELETE: {
    backgroundColor: "#fff3e0",
    color: "#e65100",
    border: "1px solid #ffe0b2",
  },
  BULK_CREATE: {
    backgroundColor: "#e0f2f1",
    color: "#00695c",
    border: "1px solid #b2dfdb",
  },
  BULK_UPDATE: {
    backgroundColor: "#f3e5f5",
    color: "#6a1b9a",
    border: "1px solid #e1bee7",
  },
  SWITCH_TO_POS: {
    backgroundColor: "#eff6ff",
    color: "#1d4ed8",
    border: "1px solid #bfdbfe",
  },
  SWITCH_TO_SUPERADMIN: {
    backgroundColor: "#faf5ff",
    color: "#7e22ce",
    border: "1px solid #e9d5ff",
  },
  DISPLAY_TIMEZONE_CHANGED: {
    backgroundColor: "#ecfeff",
    color: "#0f766e",
    border: "1px solid #99f6e4",
  },
};

export const MODULE_CONFIGS = {
  Invoice: {
    label: "Invoice",
    plural: "Invoices",
    color: "#1e293b",
    bgColor: "#f1f5f9",
    borderColor: "#cbd5e1",
  },
  StockAdjustment: {
    label: "Stock Adjustment",
    plural: "Stock Adjustments",
    color: "#0369a1",
    bgColor: "#e0f2fe",
    borderColor: "#bae6fd",
  },
  Item: {
    label: "Item",
    plural: "Items",
    color: "#0f766e",
    bgColor: "#ccfbf1",
    borderColor: "#99f6e4",
  },
  // Server-side this is the "User" module (see server/utils/activityLogger.js
  // logUserAuth) - it only ever logs LOGIN_SUCCESS/LOGOUT_SUCCESS/
  // SIGNUP_SUCCESS, so "Login" reads clearer here than the raw module name.
  User: {
    label: "Login",
    plural: "Login Activity",
    color: "#7c3aed",
    bgColor: "#f5f3ff",
    borderColor: "#ddd6fe",
  },
  Purchase: {
    label: "Purchase",
    plural: "Purchases",
    color: "#d97706",
    bgColor: "#fffbeb",
    borderColor: "#fde68a",
  },
  Return: {
    label: "Return",
    plural: "Returns",
    color: "#e11d48",
    bgColor: "#fff1f2",
    borderColor: "#fecdd3",
  },
  Staff: {
    label: "Staff",
    plural: "Staff",
    color: "#6d28d9",
    bgColor: "#f5f3ff",
    borderColor: "#ddd6fe",
  },
  Commission: {
    label: "Commission",
    plural: "Commission",
    color: "#a16207",
    bgColor: "#fefce8",
    borderColor: "#fef08a",
  },
  Payment: {
    label: "Payment",
    plural: "Payments",
    color: "#047857",
    bgColor: "#ecfdf5",
    borderColor: "#a7f3d0",
  },
  Supplier: {
    label: "Supplier",
    plural: "Suppliers",
    color: "#854d0e",
    bgColor: "#fef9c3",
    borderColor: "#fde047",
  },
  Report: {
    label: "Report",
    plural: "Reports",
    color: "#1e40af",
    bgColor: "#eff6ff",
    borderColor: "#bfdbfe",
  },
  Settings: {
    label: "Settings",
    plural: "Settings",
    color: "#475569",
    bgColor: "#f1f5f9",
    borderColor: "#e2e8f0",
  },
  Security: {
    label: "Security",
    plural: "Security",
    color: "#b91c1c",
    bgColor: "#fef2f2",
    borderColor: "#fecaca",
  },
  System: {
    label: "System",
    plural: "System",
    color: "#334155",
    bgColor: "#f8fafc",
    borderColor: "#e2e8f0",
  },
};

export const isItemChangeAction = (log) =>
  log.module === "Item" && (log.action === "UPDATE" || log.action === "BULK_UPDATE");

export const bulkSummaryLabel = (log) => {
  const count = log.details?.itemCount ?? (Array.isArray(log.items) ? log.items.length : 0);
  const plural = count === 1 ? "Item" : "Items";
  if (log.action === "BULK_CREATE") return `${count} ${plural} Added`;
  if (log.action === "BULK_UPDATE") return `${count} ${plural} Updated`;
  return null;
};

export const formatReference = (log) => {
  if (!log) {
    return {
      prefix: "",
      number: "—",
      formatted: "—",
      isBulk: false,
      color: "#64748b",
      bgColor: "#f8fafc",
      borderColor: "#e2e8f0",
    };
  }

  const bulkLabel = bulkSummaryLabel(log);
  if (bulkLabel) {
    return {
      prefix: "",
      number: bulkLabel,
      formatted: bulkLabel,
      isBulk: true,
      color: "#0f766e",
      bgColor: "#ccfbf1",
      borderColor: "#99f6e4",
    };
  }

  const raw = String(log.referenceNumber || "").trim();
  if (!raw) {
    return {
      prefix: "",
      number: "—",
      formatted: "—",
      isBulk: false,
      color: "#64748b",
      bgColor: "#f8fafc",
      borderColor: "#e2e8f0",
    };
  }

  const moduleName = log.module || "";
  const action = log.action || "";

  // Modules whose referenceNumber is an email/identifier, not a document
  // number (e.g. "superAdmin@gmail.com" for a User login) - skip the
  // prefix-guessing heuristics below entirely, since e.g. an email starting
  // with "sup..." would otherwise be misread as a Supplier "SUP-" reference.
  if (["User", "Security", "Settings", "System"].includes(moduleName)) {
    return {
      prefix: "",
      number: raw,
      formatted: raw,
      isBulk: false,
      color: "#334155",
      bgColor: "#f8fafc",
      borderColor: "#e2e8f0",
    };
  }

  // 1. Goods Return (either via action or explicit RET prefix)
  if (action.startsWith("GOODS_RETURN_") || /^RET[\s\-_:]*/i.test(raw)) {
    const cleanNum = raw.replace(/^RET[\s\-_:]*/i, "").trim();
    const val = cleanNum || raw;
    return {
      prefix: "RET",
      number: val,
      formatted: `RET - ${val}`,
      isBulk: false,
      color: "#e11d48",
      bgColor: "#fff1f2",
      borderColor: "#fecdd3",
    };
  }

  // 2. Invoice
  if (moduleName === "Invoice" || /^INV[\s\-_:]*/i.test(raw)) {
    const cleanNum = raw.replace(/^INV[\s\-_:]*/i, "").trim();
    const val = cleanNum || raw;
    return {
      prefix: "INV",
      number: val,
      formatted: `INV - ${val}`,
      isBulk: false,
      color: "#4f46e5",
      bgColor: "#eef2ff",
      borderColor: "#c7d2fe",
    };
  }

  // 3. Stock Adjustment
  if (moduleName === "StockAdjustment" || /^(STK|ADJ)[\s\-_:]*/i.test(raw)) {
    const cleanNum = raw.replace(/^(STK|ADJ)[\s\-_:]*/i, "").trim();
    const val = cleanNum || raw;
    return {
      prefix: "STK",
      number: val,
      formatted: `STK - ${val}`,
      isBulk: false,
      color: "#0284c7",
      bgColor: "#f0f9ff",
      borderColor: "#bae6fd",
    };
  }

  // 4. Item
  if (moduleName === "Item" || /^ITM[\s\-_:]*/i.test(raw)) {
    const cleanNum = raw.replace(/^ITM[\s\-_:]*/i, "").trim();
    const val = cleanNum || raw;
    return {
      prefix: "ITM",
      number: val,
      formatted: `ITM - ${val}`,
      isBulk: false,
      color: "#0f766e",
      bgColor: "#ccfbf1",
      borderColor: "#99f6e4",
    };
  }

  // 5. Purchase
  if (moduleName === "Purchase" || /^PUR[\s\-_:]*/i.test(raw)) {
    const cleanNum = raw.replace(/^PUR[\s\-_:]*/i, "").trim();
    const val = cleanNum || raw;
    return {
      prefix: "PUR",
      number: val,
      formatted: `PUR - ${val}`,
      isBulk: false,
      color: "#d97706",
      bgColor: "#fffbeb",
      borderColor: "#fde68a",
    };
  }

  // 6. Supplier
  if (moduleName === "Supplier" || /^SUP[\s\-_:]*/i.test(raw)) {
    const cleanNum = raw.replace(/^SUP[\s\-_:]*/i, "").trim();
    const val = cleanNum || raw;
    return {
      prefix: "SUP",
      number: val,
      formatted: `SUP - ${val}`,
      isBulk: false,
      color: "#854d0e",
      bgColor: "#fef9c3",
      borderColor: "#fde047",
    };
  }

  // 7. Customer / Client
  if (moduleName === "Customer" || moduleName === "Client" || /^(CUST|CLI)[\s\-_:]*/i.test(raw)) {
    const cleanNum = raw.replace(/^(CUST|CLI)[\s\-_:]*/i, "").trim();
    const val = cleanNum || raw;
    return {
      prefix: "CUST",
      number: val,
      formatted: `CUST - ${val}`,
      isBulk: false,
      color: "#1e40af",
      bgColor: "#eff6ff",
      borderColor: "#bfdbfe",
    };
  }

  // 8. Payment / Meeting
  if (moduleName === "Payment" || moduleName === "Meeting" || /^(PAY|REC)[\s\-_:]*/i.test(raw)) {
    const cleanNum = raw.replace(/^(PAY|REC)[\s\-_:]*/i, "").trim();
    const val = cleanNum || raw;
    return {
      prefix: "PAY",
      number: val,
      formatted: `PAY - ${val}`,
      isBulk: false,
      color: "#047857",
      bgColor: "#ecfdf5",
      borderColor: "#a7f3d0",
    };
  }

  return {
    prefix: "",
    number: raw,
    formatted: raw,
    isBulk: false,
    color: "#334155",
    bgColor: "#f8fafc",
    borderColor: "#e2e8f0",
  };
};

export const actionLabel = (action) =>
  ACTION_OPTIONS.find((o) => o.value === action)?.label || action || "—";

export const humanizeKey = (key) => {
  if (!key) return "";
  const customLabels = {
    subTotal: "Subtotal",
    totalAmount: "Total Amount",
    itemCount: "Item Count",
    paymentMode: "Payment Mode",
    supplierName: "Supplier Name",
    supplierId: "Supplier ID",
    clientName: "Customer Name",
    clientId: "Customer ID",
    customerName: "Customer Name",
    userName: "User Name",
    userId: "User ID",
    ipAddress: "IP Address",
    deleteReason: "Delete Reason",
    deleteReasonNote: "Delete Note",
    refundMethod: "Refund Method",
    returnType: "Return Type",
    adjustType: "Adjustment Type",
    stockTracked: "Track Stock",
    salePrice: "Sale Price",
    cost: "Cost",
    rate: "Rate",
    quantity: "Quantity",
    itemSummary: "Items Summary",
    previousItemSummary: "Previous Items",
    paymentsCollected: "Payments Collected",
    stockReversed: "Stock Reversed",
    vat: "Tax (VAT)",
    vatRate: "Tax Rate",
    discount: "Discount",
  };
  if (customLabels[key]) return customLabels[key];
  return key
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (s) => s.toUpperCase())
    .trim();
};

export const isObjectIdHex = (val) => {
  if (typeof val !== "string") return false;
  return /^[a-fA-F0-9]{24}$/.test(val.trim());
};

export const filterDisplayMetadata = (details = {}, log = {}) => {
  const entries = Object.entries(details).filter(([key]) => key !== "changes");

  const redundantIdKeys = new Set();
  if (details.supplierName || log.details?.supplierName) {
    redundantIdKeys.add("supplierId");
    redundantIdKeys.add("supplier_id");
  }
  if (details.clientName || details.customerName || log.details?.clientName) {
    redundantIdKeys.add("clientId");
    redundantIdKeys.add("client_id");
    redundantIdKeys.add("customerId");
  }
  if (log.userName || details.userName) {
    redundantIdKeys.add("userId");
    redundantIdKeys.add("user_id");
  }
  if (log.referenceNumber) {
    redundantIdKeys.add("referenceId");
    redundantIdKeys.add("invoiceId");
  }
  redundantIdKeys.add("_id");
  redundantIdKeys.add("creatorId");

  return entries.filter(([key, value]) => {
    if (redundantIdKeys.has(key)) return false;
    // Don't show redundant raw hex ID if a name exists or key ends with Id
    if ((key.toLowerCase().endsWith("id") || key === "referenceId") && isObjectIdHex(value)) {
      return false;
    }
    return true;
  });
};

// `timeZone` is optional and only ever passed by the SuperAdmin shell (see
// Context/TimezoneContext.js) - every other caller (a regular store's own
// Activity Logs page) omits it, which keeps `undefined` as the `timeZone`
// option below and preserves the exact previous behavior: format in
// whatever zone the viewer's own browser/OS is set to.
export const formatDateTime = (dateStr, timeZone) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleString(undefined, {
    timeZone: timeZone || undefined,
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
};

export const formatRelativeTime = (dateStr, timeZone) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString(undefined, { timeZone: timeZone || undefined, month: "short", day: "numeric" });
};

export const getInitials = (name) => {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

export const formatClientIp = (ip) => {
  if (ip == null || ip === "") return "-";
  let value = String(ip).trim();
  if (value.toLowerCase().startsWith("::ffff:")) value = value.slice(7);
  if (value === "::1" || value === "0:0:0:0:0:0:0:1") return "127.0.0.1";
  return value;
};
